import {type RefObject, useCallback, useEffect, useRef} from 'react';
import {getDocument} from '../lib/utils';

type OutsideClickHandler = (event: Event) => void;

type UseOutsideClickProps<T extends Element> = {
  elementRef: RefObject<T> | null;
  onOutsideClick: OutsideClickHandler | null;
  triggerRef?: RefObject<Element> | null;
  isDisabled?: boolean;
  nested?: string[] | string;
};

export function useOutsideClick<T extends Element>({
  elementRef,
  onOutsideClick,
  triggerRef,
  isDisabled,
  nested,
}: UseOutsideClickProps<T>) {
  const handleOutsideClick = useRef(onOutsideClick);
  handleOutsideClick.current = onOutsideClick;

  const checkNestedElements = useCallback(
    (event: Event, ownerDocument: Document) => {
      const checkIsElementClickedBySelector = (selector: string) => {
        const nestedElement = ownerDocument.querySelector(selector);
        return nestedElement?.contains(event.target as Node);
      };

      const checkDataAttribute = () => {
        const target = event.target;
        const HTMLElementConstructor =
          ownerDocument.defaultView?.HTMLElement ?? HTMLElement;

        if (!target || !(target instanceof HTMLElementConstructor)) {
          return false;
        }

        let current: HTMLElement | null = target;
        while (current) {
          if (current.hasAttribute('data-exclude-from-outside-click')) {
            return true;
          }
          current = current.parentElement;
        }
        return false;
      };

      if (nested) {
        if (typeof nested === 'string') {
          if (checkIsElementClickedBySelector(nested)) return true;
        } else {
          if (nested.some(checkIsElementClickedBySelector)) return true;
        }
      }

      return checkDataAttribute();
    },
    [nested],
  );

  useEffect(() => {
    if (isDisabled || !handleOutsideClick.current) {
      return;
    }

    const ownerDocument =
      elementRef?.current?.ownerDocument ??
      triggerRef?.current?.ownerDocument ??
      getDocument();

    function handleClickOutside(event: Event) {
      const target = event.target;
      const NodeConstructor = ownerDocument.defaultView?.Node ?? Node;

      if (!target || !(target instanceof NodeConstructor)) {
        return;
      }

      const targetNode = target as Node;
      const isNestedElement = checkNestedElements(event, ownerDocument);

      if (
        elementRef?.current &&
        !elementRef.current.contains(targetNode) &&
        !triggerRef?.current?.contains(targetNode) &&
        !isNestedElement
      ) {
        handleOutsideClick.current?.(event);
      }
    }

    ownerDocument.addEventListener('mousedown', handleClickOutside, true);
    ownerDocument.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      ownerDocument.removeEventListener('mousedown', handleClickOutside, true);
      ownerDocument.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [checkNestedElements, elementRef, isDisabled, triggerRef]);
}
